import { presignURLQueryOptions } from "@app/hooks/s3";
import { modularTemplateQueryOptions } from "@app/hooks/mogrtTemplates";

export const PV_SCENES_PER_MODULE = 4;

export const hexToMogrtColor = (hex) => {
  if (hex == null || hex === "") return "";
  return String(hex).replace(/^#/, "").toUpperCase();
};

/** Base units that fully fit after reserving the last 4 scenes for ClosingUnit. */
export const getBaseUnitCount = (scenes) => {
  if (!Array.isArray(scenes) || scenes.length < PV_SCENES_PER_MODULE) {
    return 0;
  }
  const baseSceneCount = scenes.length - PV_SCENES_PER_MODULE;
  return Math.floor(baseSceneCount / PV_SCENES_PER_MODULE);
};

export const buildModulesFromScenes = (scenes = [], templateModules = []) => {
  const byName = Object.fromEntries(
    (Array.isArray(templateModules) ? templateModules : []).map((module) => [
      module.name,
      module,
    ]),
  );

  const intro = byName.Intro;
  const baseUnit = byName.BaseUnit;
  const closingUnit = byName.ClosingUnit;

  const baseUnitCount = getBaseUnitCount(scenes);

  return [
    intro,
    ...Array.from({ length: baseUnitCount }, () => ({ ...baseUnit })),
    closingUnit,
  ].map(({ name, description, rootPath, duration }) => ({
    name,
    description,
    rootPath,
    duration,
  }));
};

/**
 * Map saved scenes to render modules: 1 Intro (no scenes), N×BaseUnit (4 scenes each),
 * 1 ClosingUnit (last 4 scenes).
 */
export const assignScenesToModules = (scenes) => {
  if (!Array.isArray(scenes) || scenes.length === 0) {
    return [
      { name: "Intro", scenes: [] },
      { name: "ClosingUnit", scenes: [] },
    ];
  }

  if (scenes.length < PV_SCENES_PER_MODULE) {
    return [
      { name: "Intro", scenes: [] },
      { name: "ClosingUnit", scenes },
    ];
  }

  const assignments = [{ name: "Intro", scenes: [] }];
  const closingScenes = scenes.slice(-PV_SCENES_PER_MODULE);
  const basePool = scenes.slice(0, -PV_SCENES_PER_MODULE);
  const baseUnitCount = Math.floor(basePool.length / PV_SCENES_PER_MODULE);

  for (let index = 0; index < baseUnitCount; index++) {
    assignments.push({
      name: "BaseUnit",
      scenes: basePool.slice(
        index * PV_SCENES_PER_MODULE,
        index * PV_SCENES_PER_MODULE + PV_SCENES_PER_MODULE,
      ),
    });
  }

  assignments.push({ name: "ClosingUnit", scenes: closingScenes });
  return assignments;
};

export const collectTemplateVideoAssets = (templateData, videoData) => {
  const verticals =
    templateData?.verticals ?? templateData?.["verticals-tmp"] ?? [];
  const verticalKey = videoData?.verticalKey;
  const vertical =
    verticals.find((item) => item.key === verticalKey) ??
    verticals.find((item) => item.isDefault) ??
    verticals[0];

  const verticalAssets = vertical?.assets ?? [];
  const uploadedVideoAssets = (videoData?.assets ?? []).filter(
    (asset) =>
      asset?.type === "video" ||
      asset?.filePath?.toLowerCase().endsWith(".mp4") ||
      asset?.name?.startsWith("GENERIC-"),
  );

  const byName = new Map();
  for (const asset of [...verticalAssets, ...uploadedVideoAssets]) {
    if (asset?.name) {
      byName.set(asset.name, asset);
    }
    if (asset?.filePath) {
      byName.set(asset.filePath, asset);
      const baseName = asset.filePath
        .split("/")
        .pop()
        ?.replace(/\.mp4$/i, "");
      if (baseName) {
        byName.set(baseName, asset);
      }
    }
  }

  return Array.from(byName.values());
};

const resolveVideoAsset = async (videoRef, context) => {
  if (!videoRef) return null;

  const ref = String(videoRef).trim();
  const { assets = [], queryClient, templateVideoAssets = [] } = context;

  const matched =
    assets.find(
      (asset) =>
        asset.name === ref ||
        asset.filePath === ref ||
        asset.filePath?.endsWith(`/${ref}.mp4`) ||
        asset.filePath?.endsWith(`/${ref}`),
    ) ??
    templateVideoAssets.find(
      (asset) =>
        asset.name === ref ||
        asset.filePath === ref ||
        asset.filePath?.endsWith(`/${ref}.mp4`) ||
        asset.filePath?.endsWith(`/${ref}`),
    );

  if (matched) {
    if (matched.url) {
      return { url: matched.url };
    }

    if (matched.filePath && queryClient) {
      const s3Asset = await queryClient.query(
        presignURLQueryOptions(matched.filePath),
      );
      return { url: s3Asset.presignURL };
    }
  }

  // No known asset: treat the ref as a direct S3 key and presign it the same
  // way image assets are (e.g. "mogrt/footage/generic/GENERIC-...mp4").
  if (
    queryClient &&
    (/\.(mp4|mov|webm|m4v)$/i.test(ref) || ref.includes("/"))
  ) {
    const s3Asset = await queryClient.query(presignURLQueryOptions(ref));
    return { url: s3Asset.presignURL };
  }

  return null;
};

/**
 * Single source of truth for the PV scene → mogrt control mapping. Shared by the
 * render payload builder (`applyScenesToModuleControls`) and the export.json
 * filler (`fillExportJsonWithScenes`) so the control label vocabulary and the
 * per-field scene mapping live in one place and cannot drift apart.
 *
 * Each entry is `{ label, kind, value }` where `kind` tells the consumer how to
 * treat the raw value:
 *   - "text"  → a plain string
 *   - "video" → a scene video *reference*; consumers resolve it to a presigned
 *               URL (render) or a file name (export).
 *
 * @param {object} scene - A single PV scene ({ Text, Motion, Color[], Video[] }).
 * @param {number} sceneNumber - 1-based scene position within the module.
 * @returns {Array<{ label: string, kind: "text"|"video", value: * }>}
 */
export const sceneControlEntries = (scene, sceneNumber) => [
  { label: `Scene${sceneNumber}-Text`, kind: "text", value: scene.Text },
  {
    label: `Scene${sceneNumber}-Text-Motion`,
    kind: "text",
    value: scene.Motion,
  },
  {
    label: `Scene${sceneNumber}-Color1`,
    kind: "text",
    value: hexToMogrtColor(scene.Color?.[0]),
  },
  {
    label: `Scene${sceneNumber}-Color2`,
    kind: "text",
    value: hexToMogrtColor(scene.Color?.[1] ?? scene.Color?.[0]),
  },
  {
    label: `Scene${sceneNumber}-Video1`,
    kind: "video",
    value: scene.Video?.[0],
  },
  {
    label: `Scene${sceneNumber}-Video2`,
    kind: "video",
    value: scene.Video?.[1],
  },
];

export const applyScenesToModuleControls = async (
  scenes,
  controls,
  context,
) => {
  if (!Array.isArray(scenes) || !Array.isArray(controls) || !scenes.length) {
    return {};
  }

  const controlByLabel = new Map(
    controls.map((control) => [control.label ?? "", control]),
  );
  const formValues = {};

  for (
    let index = 0;
    index < Math.min(scenes.length, PV_SCENES_PER_MODULE);
    index++
  ) {
    const scene = scenes[index] ?? {};
    const sceneNumber = index + 1;

    for (const { label, kind, value } of sceneControlEntries(
      scene,
      sceneNumber,
    )) {
      const control = controlByLabel.get(label);
      if (!control) continue;

      if (kind === "video") {
        if (!value) continue;
        const media = await resolveVideoAsset(value, context);
        if (media) {
          formValues[control.variableId] = media;
        }
      } else if (label.endsWith("-Text")) {
        const text = value ?? control.defaultData?.text ?? "";
        if (text !== "") {
          formValues[control.variableId] = text;
        }
      } else if (label.endsWith("-Text-Motion")) {
        formValues[control.variableId] =
          value ?? control.defaultData?.text ?? "Subtle";
      } else {
        // Color1 / Color2
        formValues[control.variableId] =
          value || control.defaultData?.text || "";
      }
    }
  }

  return formValues;
};

/** Basename of a video reference (asset name, filePath, or S3 key). */
const videoFileName = (ref) => {
  if (ref == null || ref === "") return "";
  return String(ref).split("/").pop() ?? "";
};

/**
 * Fill a module's parsed export.json with that module's assigned scene data.
 *
 * Uses the same `sceneControlEntries` mapping as `applyScenesToModuleControls`,
 * but writes the export.json shapes instead of render form values:
 *   - text controls   → `item.Text["Source Text"]` (Text / Text-Motion / Color1 / Color2)
 *   - video controls  → `item.Path["Source"]` (Video1 / Video2), the video file name used
 *   - logo control    → `item.Path["Source"]` (`Logo`), the logo file name used
 *
 * The `Logo` control is a base (non-scene) value, so it is filled independently
 * of the scenes array — this is what populates the scene-less Intro module.
 *
 * Values are only written when they are provided and the export item exposes the
 * matching shape, so template defaults (e.g. "Subtle", "EB1000") survive for
 * empty scenes.
 *
 * @param {object} exportTemplate - Parsed export.json: `{ [topKey]: { [label]: { Text|Path: {...} } } }`.
 * @param {Array} scenes - Scenes assigned to this module (capped at PV_SCENES_PER_MODULE).
 * @param {object} [options]
 * @param {string} [options.logoFileName] - Logo file name for the `Logo` control (any module).
 * @returns {object} A filled clone of `exportTemplate` (input is not mutated).
 */
export const fillExportJsonWithScenes = (
  exportTemplate,
  scenes,
  { logoFileName } = {},
) => {
  if (!exportTemplate || typeof exportTemplate !== "object") {
    return exportTemplate;
  }
  const topKey = Object.keys(exportTemplate)[0];
  if (!topKey) return exportTemplate;

  const cloned = JSON.parse(JSON.stringify(exportTemplate));
  const items = cloned[topKey];
  if (!items || typeof items !== "object") return cloned;

  const setSourceText = (label, value) => {
    if (value == null || value === "") return;
    const item = items[label];
    if (item?.Text?.["Source Text"] !== undefined) {
      item.Text["Source Text"] = String(value);
    }
  };

  const setPathSource = (label, value) => {
    if (value == null || value === "") return;
    const item = items[label];
    if (item?.Path?.["Source"] !== undefined) {
      item.Path["Source"] = String(value);
    }
  };

  // Base (non-scene) value — fills modules with a `Logo` control, incl. Intro.
  setPathSource("Logo", logoFileName);

  const list = Array.isArray(scenes) ? scenes : [];
  for (
    let index = 0;
    index < Math.min(list.length, PV_SCENES_PER_MODULE);
    index++
  ) {
    const scene = list[index] ?? {};
    const sceneNumber = index + 1;

    for (const { label, kind, value } of sceneControlEntries(
      scene,
      sceneNumber,
    )) {
      if (kind === "video") {
        setPathSource(label, videoFileName(value));
      } else {
        setSourceText(label, value);
      }
    }
  }

  return cloned;
};

export const getVideoScenes = (videoData) => {
  if (Array.isArray(videoData?.scenes) && videoData.scenes.length) {
    return videoData.scenes;
  }

  const selectedCampaign = videoData?.campaigns?.find(
    (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
  );

  const latestCampaign = videoData?.campaigns?.[videoData.campaigns.length - 1];

  return (
    selectedCampaign?.scenes ??
    latestCampaign?.scenes ??
    videoData?.campaigns?.[0]?.scenes ??
    []
  );
};

/**
 * Build the partnership-value export.json: an array of `{ mogrt, scenes }` items,
 * one per render module (in render order, matching how scenes are assigned).
 *
 *   - `mogrt`  — the module's `.mogrt` file name (basename of its S3 mogrtPath).
 *   - `scenes` — the module's export.json filled with its assigned scene data
 *                (and the Logo), or `{}` when the module has no export.json on S3.
 *
 * @param {object} args
 * @param {object} args.videoData - The video record (`video.data`).
 * @param {object} args.templateData - The template record (`template.data`).
 * @param {import("@tanstack/react-query").QueryClient} args.queryClient
 * @returns {Promise<Array<{ mogrt: string, scenes: Object }>>}
 */
export const buildModularExportJson = async ({
  videoData,
  templateData,
  queryClient,
}) => {
  const scenes = getVideoScenes(videoData);
  const resolvedModules = videoData?.modules?.length
    ? videoData.modules
    : buildModulesFromScenes(scenes, templateData?.modules);
  const sceneAssignments = assignScenesToModules(scenes);

  // Logo is resolved the way the modular render resolves it (mapTaggedValue):
  // prefer the cropped variant, fall back to the raw "Logo" asset.
  const logoAsset =
    videoData?.assets?.find((asset) => asset.name === "Logo_cropped") ??
    videoData?.assets?.find((asset) => asset.name === "Logo");
  const logoFileName = logoAsset?.filePath?.split("/").pop() ?? "";

  // Same order as `resolvedModules`; gives us each module's `.mogrt` path.
  const modularData = await queryClient.query(
    modularTemplateQueryOptions(resolvedModules),
  );

  return Promise.all(
    resolvedModules.map(async (moduleData, index) => {
      const mogrtPath = modularData.modules?.[index]?.mogrtPath ?? "";
      const mogrt = mogrtPath.split("/").pop() ?? "";

      const rootPath = moduleData?.rootPath;
      let sceneData = {};

      if (rootPath) {
        const exportPath = `${rootPath.replace(/\/$/, "")}/export.json`;
        try {
          const s3Data = await queryClient.query(
            presignURLQueryOptions(exportPath),
          );
          const res = await fetch(s3Data.presignURL);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          const exportTemplate = await res.json();
          const filled = fillExportJsonWithScenes(
            exportTemplate,
            sceneAssignments[index]?.scenes ?? [],
            { logoFileName },
          );
          const topKey = Object.keys(filled ?? {})[0];
          sceneData = topKey ? filled[topKey] : (filled ?? {});
        } catch (error) {
          console.debug(
            `No export.json for module "${moduleData?.name}" (${exportPath})`,
            error,
          );
        }
      }

      return { mogrt, scenes: sceneData };
    }),
  );
};
